import { useCallback, useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { useStore } from '../store/useStore';
import { parseExcelToStudentData } from '../lib/excelParser';
import { generateAllMicroReports } from '../lib/ruleEngine';
import { buildMacroData } from '../lib/macroDataBuilder';

export function ExcelUploader() {
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { setStudentDataList, setMacroData, setClassName, taskId, setTaskId } = useStore();

  const processFile = useCallback(async (file: File) => {
    setError(null);

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('请上传 Excel 文件 (.xlsx 或 .xls)');
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const studentDataList = parseExcelToStudentData(buffer);

      if (studentDataList.length === 0) {
        setError('文件中没有找到学生数据');
        return;
      }

      const firstStudent = studentDataList[0];
      const className = firstStudent.student.className;
      setClassName(className);

      if (!taskId) {
        setTaskId(crypto.randomUUID());
      }

      const enrichedData = generateAllMicroReports(studentDataList);
      setStudentDataList(enrichedData);
      setSuccess(`成功解析 ${studentDataList.length} 名学生的数据！`);

      const macroData = buildMacroData(
        taskId || crypto.randomUUID(),
        className,
        enrichedData
      );
      setMacroData(macroData);
    } catch (err) {
      setError(`文件解析失败: ${err instanceof Error ? err.message : '未知错误'}`);
    }
  }, [taskId, setClassName, setTaskId, setStudentDataList, setMacroData]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <FileSpreadsheet className="w-8 h-8 text-green-600" />
          上传评价数据
        </h2>

        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          <Upload className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className="text-lg text-gray-600 mb-4">
            拖拽 Excel 文件到此处，或点击选择文件
          </p>
          <label className="inline-block cursor-pointer bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
            选择文件
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">Excel 文件要求</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• 表头包含：学号、姓名、班级、组别</li>
            <li>• 自评题目：自评Q1~自评Q8、综合评价</li>
            <li>• 互评题目：互评Q1~互评Q4</li>
            <li>• 师评题目：师评Q1~师评Q4</li>
          </ul>
        </div>
      </div>
    </div>
  );
}